import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import {
  ShieldCheck,
  Download,
  Terminal,
  Lock,
  ServerOff,
  ArrowRight,
  FileDown,
  Copy,
  Check,
  Mic,
  AlertCircle,
  CheckCircle2,
  Wand2,
} from 'lucide-react';
import { GovernmentDisclaimer } from '../components/GovernmentDisclaimer';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * SelfHostPage — marketing + download for the docker-compose bundle.
 *
 * Same reader profile as the manifesto (security-aware, probably about
 * to evaluate whether to run their own), but this page gets explicit
 * about WHAT they're getting and the five-minute path to running it.
 *
 * We fetch `/api/selfhost/info` for metadata so the download button
 * reflects the real bundle size.
 */

const Step = ({ n, title, children }) => (
  <div className="flex gap-4">
    <div className="w-10 h-10 shrink-0 rounded-full bg-primary/10 border border-primary/30 text-primary font-bold flex items-center justify-center">
      {n}
    </div>
    <div className="flex-1 pt-1.5">
      <h3
        className="text-lg font-semibold text-white tracking-tight mb-1.5"
        style={{ fontFamily: 'Outfit, sans-serif' }}
      >
        {title}
      </h3>
      <div className="text-sm text-slate-400 leading-relaxed">{children}</div>
    </div>
  </div>
);

const Code = ({ children, ...rest }) => {
  const [copied, setCopied] = useState(false);
  const text = typeof children === 'string' ? children : '';
  const onCopy = async () => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {}
  };
  return (
    <div className="relative my-2">
      <pre
        className="p-3 pr-10 rounded-lg bg-secondary/30 border border-border text-xs text-slate-200 font-mono overflow-x-auto"
        {...rest}
      >
        <code>{children}</code>
      </pre>
      <button
        onClick={onCopy}
        className="absolute top-2 right-2 p-1.5 rounded hover:bg-secondary/50 text-slate-500 hover:text-slate-300"
        aria-label="Copy"
      >
        {copied ? (
          <Check className="w-3.5 h-3.5 text-emerald-400" />
        ) : (
          <Copy className="w-3.5 h-3.5" />
        )}
      </button>
    </div>
  );
};

const fmtBytes = (b) => {
  if (!b) return '';
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
  return `${(b / 1024 / 1024).toFixed(1)} MB`;
};

/* ──────────────────────────────────────────────────────────────
   LiveKit deploy helper — the copy-paste activation path
   sketched in iteration_16 for the GroupCallPanel's disabled
   state, now promoted to a full-page public surface so self-
   hosting operators can share the URL in Matrix rooms / GitHub
   issues instead of screenshotting terminal output.
   ────────────────────────────────────────────────────────────── */

const LIVEKIT_ENV_BLOCK = `# LiveKit SFU — voice-call relay for 3+ participants.
# Generate a 32-byte secret: openssl rand -base64 32
LIVEKIT_URL=wss://voice.yourdomain.com
LIVEKIT_API_KEY=APIgovshield_your_key_here
LIVEKIT_API_SECRET=REPLACE_WITH_32_RANDOM_BYTES_BASE64_ENCODED`;

const LIVEKIT_COMPOSE_BLOCK = `# 1. Copy the example config into place:
cp selfhost/livekit-config.yaml.example selfhost/livekit-config.yaml

# 2. Uncomment the livekit service block in selfhost/docker-compose.yml

# 3. Reverse-proxy wss://voice.yourdomain.com → livekit:7880
#    (Caddy / Nginx / Cloudflare Tunnel all work. UDP 50000-50100
#     must bypass the HTTPS-only ingress — see the config file.)

# 4. Start the stack:
cd selfhost && docker compose up -d livekit

# 5. Restart the backend to pick up the new env:
docker compose restart backend`;

/**
 * Required env vars for the LiveKit SFU path. The keys are the
 * literal env-var names we grep for; the regex ("shape") is a
 * cheap sanity check to catch the most common paste mistakes
 * (wrong URL scheme, forgotten placeholder swap). Neither is a
 * substitute for actually starting the stack — a valid-shaped
 * secret can still be wrong — but they eliminate the most
 * frustrating class of silent-failure bugs before first launch.
 */
const LIVEKIT_REQUIRED_VARS = [
  {
    key: 'LIVEKIT_URL',
    shape: /^wss?:\/\/[^\s/]+(?:\:\d+)?(?:\/.*)?$/,
    hint: 'must start with wss:// (or ws:// for local dev)',
  },
  {
    key: 'LIVEKIT_API_KEY',
    shape: /^[A-Za-z0-9_-]{6,}$/,
    hint: 'alphanumeric, at least 6 chars — no whitespace',
  },
  {
    key: 'LIVEKIT_API_SECRET',
    shape: /^(?!REPLACE_WITH).{16,}$/,
    hint: 'must be swapped from the REPLACE_WITH_ placeholder; min 16 chars',
  },
];

/**
 * Parse a pasted .env block into {key: value} and classify each
 * required var as 'present' (shape ok), 'malformed' (key set but
 * value fails shape check), or 'missing' (key absent entirely).
 * Pure function; no side effects. Trims surrounding whitespace
 * and strips inline comments ("KEY=value # comment") so paste-
 * from-docs usage works.
 */
const parseAndValidateEnv = (raw) => {
  const text = String(raw || '');
  const entries = {};
  for (const line of text.split(/\r?\n/)) {
    const trimmed = line.trim();
    if (!trimmed || trimmed.startsWith('#')) continue;
    const eq = trimmed.indexOf('=');
    if (eq < 0) continue;
    const k = trimmed.slice(0, eq).trim();
    // Strip optional surrounding quotes + inline "#" comment.
    let v = trimmed.slice(eq + 1).trim();
    const commentIdx = v.indexOf(' #');
    if (commentIdx >= 0) v = v.slice(0, commentIdx).trim();
    if ((v.startsWith('"') && v.endsWith('"')) || (v.startsWith("'") && v.endsWith("'"))) {
      v = v.slice(1, -1);
    }
    entries[k] = v;
  }
  const results = LIVEKIT_REQUIRED_VARS.map(({ key, shape, hint }) => {
    if (!(key in entries)) return { key, status: 'missing', hint };
    const val = entries[key];
    if (!val) return { key, status: 'missing', hint };
    if (!shape.test(val)) return { key, status: 'malformed', hint, value: val };
    return { key, status: 'present', hint, value: val };
  });
  return { entries, results };
};

const EnvValidator = () => {
  const [input, setInput] = useState('');
  const [result, setResult] = useState(null);
  const onValidate = () => setResult(parseAndValidateEnv(input));
  const onClear = () => { setInput(''); setResult(null); };

  return (
    <div className="space-y-4" data-testid="livekit-env-validator">
      <textarea
        value={input}
        onChange={(e) => setInput(e.target.value)}
        placeholder={"Paste your .env block here — e.g.\nLIVEKIT_URL=wss://voice.example.org\nLIVEKIT_API_KEY=APIgovshield123\nLIVEKIT_API_SECRET=..."}
        className="w-full h-36 p-3 rounded-lg bg-secondary/30 border border-border text-xs text-slate-200 font-mono resize-y focus:outline-none focus:ring-2 focus:ring-primary/40"
        data-testid="livekit-env-input"
        spellCheck="false"
      />
      <div className="flex gap-2">
        <Button
          onClick={onValidate}
          className="bg-primary hover:bg-primary/90 text-primary-foreground"
          disabled={!input.trim()}
          data-testid="livekit-env-validate-btn"
        >
          Validate
        </Button>
        {result && (
          <Button onClick={onClear} variant="ghost" className="text-slate-400">
            Clear
          </Button>
        )}
      </div>
      {result && (
        <div className="space-y-2" data-testid="livekit-env-results">
          {result.results.map((r) => (
            <div
              key={r.key}
              className={`flex items-start gap-3 p-3 rounded-lg border ${
                r.status === 'present'
                  ? 'bg-emerald-500/5 border-emerald-500/30 text-emerald-200'
                  : r.status === 'malformed'
                  ? 'bg-amber-500/5 border-amber-500/30 text-amber-200'
                  : 'bg-rose-500/5 border-rose-500/30 text-rose-200'
              }`}
              data-testid={`env-result-${r.key}`}
              data-status={r.status}
            >
              {r.status === 'present' ? (
                <CheckCircle2 className="w-4 h-4 shrink-0 mt-0.5 text-emerald-400" />
              ) : (
                <AlertCircle className={`w-4 h-4 shrink-0 mt-0.5 ${r.status === 'malformed' ? 'text-amber-400' : 'text-rose-400'}`} />
              )}
              <div className="flex-1 min-w-0">
                <div className="flex items-baseline justify-between gap-2">
                  <span className="font-mono text-xs font-semibold">{r.key}</span>
                  <span className="text-[10px] uppercase tracking-[0.18em] opacity-70">
                    {r.status}
                  </span>
                </div>
                <p className="text-[11px] leading-relaxed opacity-80 mt-0.5">
                  {r.status === 'present'
                    ? 'Looks good.'
                    : r.status === 'malformed'
                    ? `Set but doesn't look right — ${r.hint}.`
                    : `Not found in pasted block — ${r.hint}.`}
                </p>
              </div>
            </div>
          ))}
          <p className="text-[11px] text-slate-500 leading-relaxed pt-1">
            Validation is purely client-side — nothing you paste leaves your
            browser. Shape checks don't substitute for actually starting the
            stack; a well-formed secret can still be wrong.
          </p>
        </div>
      )}
    </div>
  );
};

export const SelfHostPage = () => {
  const [info, setInfo] = useState(null);
  // The LiveKit .env block is stateful so the "Generate a secret
  // for me" button can swap the REPLACE_WITH_ placeholder for a
  // real 32-byte base64 value without re-rendering the whole
  // section. Default = the static template imported at module
  // scope; callers mutate via `setLivekitEnv(newBlock)`.
  const [livekitEnv, setLivekitEnv] = useState(LIVEKIT_ENV_BLOCK);

  /**
   * Mint a fresh 32-byte secret, base64-encode it, and splice it
   * into the .env block by line-wise replacing the
   * `LIVEKIT_API_SECRET=...` line. We deliberately DO NOT regex-
   * match the placeholder text itself — if an operator has
   * already pasted a real secret, this button should still
   * generate a fresh one (they might be rotating), so the line-
   * level match is the correct semantic. Each click yields a
   * brand-new secret; the button is safe to mash repeatedly.
   *
   * The secret is generated client-side via Web Crypto, never
   * leaves the page, and the .env block lives in component
   * state only — reloading /selfhost resets to the placeholder.
   */
  const handleGenerateSecret = () => {
    const raw = new Uint8Array(32);
    crypto.getRandomValues(raw);
    // Standard base64 via btoa over a binary-string conversion.
    // btoa can't take a Uint8Array directly, but it handles
    // Latin-1 strings; String.fromCharCode on a 0-255 byte value
    // is exactly that, so the round-trip is clean.
    let bin = '';
    for (const b of raw) bin += String.fromCharCode(b);
    const b64 = btoa(bin);
    const updated = livekitEnv
      .split('\n')
      .map((line) => (line.startsWith('LIVEKIT_API_SECRET=') ? `LIVEKIT_API_SECRET=${b64}` : line))
      .join('\n');
    setLivekitEnv(updated);
    toast.success('Secret generated — copy the block now');
  };

  /**
   * Package the current .env state into a real downloadable file
   * named `selfhost.env` so the operator can `mv` it straight into
   * `./selfhost/.env` without opening an editor. Zero network
   * traffic — all client-side Blob + synthetic `<a download>`.
   *
   * `URL.revokeObjectURL` runs on a microtask tick so the browser
   * has had time to start the download before the URL becomes
   * invalid; 0-ms timeout is sufficient (the click() call is
   * synchronous and the download-dispatch happens inside it).
   */
  const handleDownloadEnv = () => {
    try {
      const blob = new Blob([livekitEnv], { type: 'text/plain;charset=utf-8' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'selfhost.env';
      // Some browsers drop the download if the anchor isn't in
      // the DOM tree. Append-click-remove is the safe pattern.
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 0);
      toast.success('Downloaded selfhost.env');
    } catch {
      toast.error('Download blocked — copy the block manually');
    }
  };

  useEffect(() => {
    axios
      .get(`${API}/selfhost/info`)
      .then((r) => setInfo(r.data))
      .catch(() => setInfo({ available: false }));
  }, []);

  const download = () => {
    // Point the <a> directly at the API endpoint so the browser handles
    // the Content-Disposition filename correctly.
    window.location.href = `${API}/selfhost/bundle.zip`;
    toast.success('Downloading self-host bundle');
  };

  return (
    <div className="min-h-screen" style={{ backgroundColor: '#05050A' }}>
      {/* Sticky nav */}
      <header className="sticky top-0 z-10 backdrop-blur bg-[#05050A]/80 border-b border-border">
        <div className="max-w-5xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link
            to="/"
            className="flex items-center gap-2 text-white hover:text-primary transition-colors"
          >
            <ShieldCheck className="w-6 h-6 text-primary" />
            <span
              className="font-bold tracking-tight"
              style={{ fontFamily: 'Outfit, sans-serif' }}
            >
              Government Shield
            </span>
          </Link>
          <div className="flex items-center gap-2">
            <Link to="/manifesto">
              <Button variant="ghost" size="sm" className="text-slate-300">
                Manifesto
              </Button>
            </Link>
            <Link to="/login">
              <Button variant="ghost" size="sm" className="text-slate-300">
                Sign in
              </Button>
            </Link>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="max-w-5xl mx-auto px-6 py-16 lg:py-24">
        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-primary/10 border border-primary/20 mb-6">
          <Terminal className="w-3.5 h-3.5 text-primary" />
          <span className="text-xs text-primary uppercase tracking-wider font-medium">
            Self-host in 5 minutes
          </span>
        </div>
        <h1
          className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white tracking-tight leading-[1.05] mb-6"
          style={{ fontFamily: 'Outfit, sans-serif' }}
        >
          Run your own Government Shield
          <br />
          <span className="text-primary">on your own hardware.</span>
        </h1>
        <p className="text-lg sm:text-xl text-slate-400 leading-relaxed max-w-2xl mb-8">
          Download a tiny bundle. Double-click a launcher. The first
          person who registers becomes the permanent owner of that
          server — you, your community, your rules.
        </p>
        <div className="flex flex-wrap gap-3">
          <Button
            size="lg"
            onClick={download}
            disabled={!info?.available}
            data-testid="selfhost-download-btn"
          >
            <Download className="w-4 h-4 mr-2" />
            Download self-host bundle
            {info?.total_bytes && (
              <span className="ml-2 opacity-60 text-sm">
                · {fmtBytes(info.total_bytes)}
              </span>
            )}
          </Button>
          <a href="#steps">
            <Button variant="outline" size="lg">
              See the 5 steps
            </Button>
          </a>
        </div>
      </section>

      {/* What's in the bundle */}
      <section className="max-w-5xl mx-auto px-6 py-12">
        <div className="grid md:grid-cols-3 gap-5">
          <div className="p-5 rounded-xl bg-card/40 backdrop-blur border border-border">
            <FileDown className="w-6 h-6 text-primary mb-3" strokeWidth={1.75} />
            <h3 className="text-white font-semibold mb-1">Docker Compose</h3>
            <p className="text-sm text-slate-400 leading-relaxed">
              A single <code className="text-xs font-mono">docker-compose.yml</code>{' '}
              that wires up the backend and MongoDB in an isolated network.
            </p>
          </div>
          <div className="p-5 rounded-xl bg-card/40 backdrop-blur border border-border">
            <Terminal className="w-6 h-6 text-primary mb-3" strokeWidth={1.75} />
            <h3 className="text-white font-semibold mb-1">One-click launchers</h3>
            <p className="text-sm text-slate-400 leading-relaxed">
              <code className="text-xs font-mono">launch.sh</code> (macOS /
              Linux) and{' '}
              <code className="text-xs font-mono">launch.bat</code> (Windows)
              auto-generate your JWT secret, build, and start.
            </p>
          </div>
          <div className="p-5 rounded-xl bg-card/40 backdrop-blur border border-border">
            <Lock className="w-6 h-6 text-primary mb-3" strokeWidth={1.75} />
            <h3 className="text-white font-semibold mb-1">Zero telemetry</h3>
            <p className="text-sm text-slate-400 leading-relaxed">
              The bundle doesn't dial home. Your instance is invisible to us —
              we can't see user counts, uptime, message volume, or who you
              invite.
            </p>
          </div>
        </div>
      </section>

      {/* Steps */}
      <section id="steps" className="max-w-3xl mx-auto px-6 py-16 space-y-10 scroll-mt-20">
        <div>
          <p className="text-xs uppercase tracking-wider text-primary font-semibold mb-3">
            The happy path
          </p>
          <h2
            className="text-3xl lg:text-4xl font-bold text-white tracking-tight"
            style={{ fontFamily: 'Outfit, sans-serif' }}
          >
            Five steps. No DevOps degree required.
          </h2>
        </div>

        <div className="space-y-8">
          <Step n={1} title="Install Docker Desktop">
            <p>
              Grab it from{' '}
              <a
                href="https://docker.com/products/docker-desktop"
                target="_blank"
                rel="noopener noreferrer"
                className="text-primary hover:underline"
              >
                docker.com
              </a>
              . macOS, Windows, and Linux all supported. This is the only
              external dependency.
            </p>
          </Step>

          <Step n={2} title="Download the bundle">
            <p>Click the button at the top, or run:</p>
            <Code>{`curl -OL ${API}/selfhost/bundle.zip\nunzip GovernmentShield-selfhost.zip`}</Code>
          </Step>

          <Step n={3} title="Drop the backend source into ./backend/">
            <p>
              The bundle is kept tiny on purpose. Copy the Government Shield{' '}
              <code className="text-xs font-mono">backend/</code> folder
              (<code className="text-xs font-mono">server.py</code>,{' '}
              <code className="text-xs font-mono">requirements.txt</code>, and
              the <code className="text-xs font-mono">scripts/</code> folder)
              from your source checkout into{' '}
              <code className="text-xs font-mono">
                government-shield-selfhost/backend/
              </code>
              .
            </p>
          </Step>

          <Step n={4} title="Launch">
            <p>macOS / Linux:</p>
            <Code>{`cd government-shield-selfhost\n./launch.sh`}</Code>
            <p>Windows: double-click <code className="text-xs font-mono">launch.bat</code>.</p>
            <p className="mt-2 text-slate-500">
              First run creates your <code className="text-xs font-mono">.env</code>{' '}
              with a fresh 64-char JWT secret, builds the Docker image, and
              starts the stack. Healthy in ~30 seconds.
            </p>
          </Step>

          <Step n={5} title="Register — you become the owner">
            <p>
              Open{' '}
              <code className="text-xs font-mono">http://localhost:8001</code>{' '}
              in the desktop client (or the web UI, pointed at the same URL).
              Register. You're now the permanent{' '}
              <span className="text-amber-400 font-medium">owner</span> of
              that instance.
            </p>
          </Step>
        </div>

        <div className="p-4 rounded-xl bg-amber-500/5 border border-amber-500/20 text-sm text-amber-200/80">
          <p className="font-medium text-amber-300 mb-1">Going public?</p>
          <p className="leading-relaxed">
            Put a reverse proxy (Caddy, Nginx, Cloudflare Tunnel) in front
            that terminates TLS, lock down <code className="font-mono">CORS_ORIGINS</code>,
            and snapshot the <code className="font-mono">gs_mongo_data</code>{' '}
            volume regularly. The full README inside the bundle covers it.
          </p>
        </div>
      </section>

      {/* LiveKit voice SFU — deploy helper + .env validator */}
      <section className="max-w-5xl mx-auto px-6 py-16" data-testid="selfhost-livekit-section">
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-3">
            <Mic className="w-7 h-7 text-primary" strokeWidth={1.5} />
            <span className="text-[10px] uppercase tracking-[0.25em] text-primary/80 font-mono">
              Optional · Voice SFU
            </span>
          </div>
          <h2
            className="text-3xl font-bold text-white tracking-tight mb-3"
            style={{ fontFamily: 'Outfit, sans-serif' }}
          >
            Group voice calls.
          </h2>
          <p className="text-slate-400 leading-relaxed max-w-3xl">
            1:1 voice calls work out of the box on pure peer-to-peer WebRTC —
            no server, no media relay. Calls with <strong className="text-slate-200">3+ participants</strong>{' '}
            need an SFU (selective forwarding unit) because mesh topology
            eats mobile uploads for breakfast. Government Shield uses{' '}
            <a
              href="https://livekit.io"
              target="_blank"
              rel="noopener noreferrer"
              className="text-primary hover:underline"
            >
              LiveKit
            </a>
            , an Apache-2.0 open-source SFU that routes SFrame-encrypted
            frames without ever decrypting them.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Left column: config snippets */}
          <div className="space-y-4">
            <div>
              <div className="flex items-center justify-between mb-2 gap-3 flex-wrap">
                <h3 className="text-sm font-semibold text-slate-200">
                  1 · <code className="font-mono text-primary">selfhost/.env</code>
                </h3>
                <div className="flex items-center gap-3">
                  <button
                    type="button"
                    onClick={handleGenerateSecret}
                    className="inline-flex items-center gap-1.5 text-[11px] text-primary/90 hover:text-primary transition-colors"
                    data-testid="livekit-generate-secret-btn"
                    title="Fill LIVEKIT_API_SECRET with a fresh 32-byte base64 value"
                  >
                    <Wand2 className="w-3.5 h-3.5" />
                    Generate a secret for me
                  </button>
                  <button
                    type="button"
                    onClick={handleDownloadEnv}
                    className="inline-flex items-center gap-1.5 text-[11px] text-slate-300 hover:text-white transition-colors"
                    data-testid="livekit-download-env-btn"
                    title="Download the current block as selfhost.env"
                  >
                    <FileDown className="w-3.5 h-3.5" />
                    Download .env
                  </button>
                </div>
              </div>
              <Code data-testid="livekit-env-snippet">{livekitEnv}</Code>
            </div>
            <div>
              <h3 className="text-sm font-semibold text-slate-200 mb-2">
                2 · Launch
              </h3>
              <Code data-testid="livekit-compose-snippet">{LIVEKIT_COMPOSE_BLOCK}</Code>
            </div>
            <p className="text-[11px] text-slate-500 leading-relaxed">
              The server config (<code className="font-mono text-slate-400">selfhost/livekit-config.yaml.example</code>)
              ships with egress disabled, ingress disabled, and E2EE-required —
              the SFU physically cannot record or inject streams, and clients
              refuse to publish unencrypted frames. Don't change those unless
              you want to break the threat model.
            </p>
          </div>

          {/* Right column: validator */}
          <div className="rounded-xl border border-border bg-card/30 p-5">
            <h3 className="text-sm font-semibold text-slate-200 mb-1">
              Validate your .env
            </h3>
            <p className="text-[11px] text-slate-500 leading-relaxed mb-4">
              Paste the block you're about to write to disk and we'll tell you
              which of the three required vars is missing, malformed, or still
              carrying the <code className="font-mono">REPLACE_WITH_</code>{' '}
              placeholder. All checks run in your browser — nothing leaves
              this tab.
            </p>
            <EnvValidator />
          </div>
        </div>
      </section>

      {/* Philosophy recap */}
      <section className="max-w-5xl mx-auto px-6 py-16">
        <div className="rounded-3xl border border-border bg-card/30 p-8 lg:p-12">
          <ServerOff className="w-10 h-10 text-primary mb-5" strokeWidth={1.5} />
          <h2
            className="text-3xl font-bold text-white tracking-tight mb-4"
            style={{ fontFamily: 'Outfit, sans-serif' }}
          >
            Why self-host at all?
          </h2>
          <p className="text-slate-400 leading-relaxed max-w-3xl mb-6">
            The hosted preview we run is a convenience, not a promise. If
            your threat model includes subpoena, infiltration, or us getting
            a phone call from someone scary, running your own instance
            eliminates the middleman entirely. Your messages, keys, and
            user records never leave your hardware. There's no platform to
            coerce because there is no platform.
          </p>
          <Link to="/manifesto" className="inline-flex items-center gap-2 text-primary hover:underline">
            Read the principles
            <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-border">
        <div className="max-w-5xl mx-auto px-6 py-10 space-y-4">
          <div className="flex flex-wrap items-center justify-between gap-4 text-sm text-slate-500">
            <div className="flex items-center gap-2">
              <ShieldCheck className="w-4 h-4 text-primary/60" />
              <span>Government Shield</span>
            </div>
            <div className="flex gap-6">
              <Link to="/manifesto" className="hover:text-slate-300 transition-colors">
                Manifesto
              </Link>
              <Link to="/login" className="hover:text-slate-300 transition-colors">
                Sign in
              </Link>
            </div>
          </div>
          <GovernmentDisclaimer />
        </div>
      </footer>
    </div>
  );
};

export default SelfHostPage;
